import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { formatCurrency } from '@/lib/i18n';
import { DAY_KEYS } from '../lib/rule-draft';
import type { PricingRuleRow } from '../pricingListsApi';

/**
 * "SAR 30.00 per hour · Thu–Sat 18:00–02:00" — §10.2 in one line.
 *
 * `formatCurrency` rather than the raw `price` string, and that is not merely
 * tidiness: the API serialises the `Decimal(14,4)` column through `Decimal.js`'s
 * `toJSON`, which drops trailing zeros, so `'25.5000'` reaches this screen as
 * `"25.5"` and `'40.0000'` as `"40"`. Printing what arrived would show a venue a
 * rate its own bills do not use.
 */
export function useRuleLabel(currency: string) {
  const { t, i18n } = useTranslation();

  return (rule: PricingRuleRow): string => {
    const price = formatCurrency(rule.price, currency, i18n.language);
    const unit = t(`spaces.resources.unit.${rule.unit}`, { minutes: rule.durationMinutes ?? 0 });
    const parts = [`${price} ${unit}`];

    if (rule.minMinutes) parts.push(t('spaces.resources.minMinutes', { count: rule.minMinutes }));
    if (rule.daysOfWeek?.length && rule.daysOfWeek.length < 7) {
      parts.push(rule.daysOfWeek.map((day) => t(`spaces.resources.day.${DAY_KEYS[day]}`)).join(', '));
    }
    if (rule.startTime && rule.endTime) parts.push(`${rule.startTime}–${rule.endTime}`);

    return parts.join(' · ');
  };
}

export function PricingRuleList({
  rules,
  currency,
  activeRuleId,
  /**
   * What to do about there being no rule.
   *
   * "No active pricing rule" was the whole of this component's empty state, and
   * it was a dead end: a session on a resource with no rule cannot be started at
   * all, and there was nowhere in the product to add one. The caller passes the
   * way out — a button into the editor — so the sentence that reports the
   * problem is also the one that fixes it.
   */
  emptyAction,
}: {
  rules: readonly PricingRuleRow[];
  currency: string;
  activeRuleId?: string | null;
  emptyAction?: ReactNode;
}) {
  const { t } = useTranslation();
  const label = useRuleLabel(currency);
  const active = rules.filter((rule) => rule.isActive);

  if (active.length === 0) {
    return (
      <div className="space-y-2">
        <p className="text-xs text-ink-muted">{t('spaces.resources.noPricing')}</p>
        {emptyAction}
      </div>
    );
  }

  return (
    <ul className="space-y-1 text-xs">
      {active.map((rule) => (
        <li
          key={rule.id}
          className={
            rule.id === activeRuleId
              ? 'flex flex-wrap gap-x-2 font-medium text-accent'
              : 'flex flex-wrap gap-x-2 text-ink-muted'
          }
        >
          <span dir="auto">{rule.name}</span>
          <span dir="ltr">{label(rule)}</span>
        </li>
      ))}
    </ul>
  );
}
